import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { Group, Space, Stack } from '@mantine/core';
import Text from '@ui/components/Text/Text';
import CustomLogo from 'src/new_arch/modules/authentication/components/CustomLogo/CustomLogo';
import { ReactComponent as VeryBusyLogoGray } from 'src/assets/img/icons/vb-logo-gray.svg';

import WhiteBox from '../WhiteBox/WhiteBox';
import GuestViewPreviewEmailTooltip from './GuestViewPreviewEmailTooltip/GuestViewPreviewEmailTooltip';
import ErrorCard from '../../new_arch/modules/authentication/components/ErrorCard/ErrorCard';
import Button from '../../new_arch/components/Button/Button';
import VeryBusyPreviewLogo from '../../new_arch/components/VeryBusyPreviewLogo/VeryBusyPreviewLogo';
import FormInput from '../../new_arch/components/FormInput/FormInput';
import useProjectGuestViewPreviewPresenter from '../../presenters/ProjectGuestViewPreview/useProjectGuestViewPreviewPresenter';
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '../../config/urls';
import { CustomBranding } from '../../new_arch/models/customBranding';

import './ProjectGuestViewPreview.scss';

type ProjectGuestViewPreviewProps = {
	projectID: number,
	name: string,
	customBranding?: CustomBranding,
	guestHash: string
}

const ProjectGuestViewPreview =	( { projectID, name, customBranding, guestHash }: ProjectGuestViewPreviewProps ) => {
	const presenter = useProjectGuestViewPreviewPresenter( { projectID, guestHash, customBranding } );

	return (
		<div className="ProjectGuestViewPreview">
			<div className="left-side">
				{(
					presenter.showCustomLogo
						? <CustomLogo logo={presenter.customLogoSrc} alt={presenter.customLogoAlt} />
						: <VeryBusyPreviewLogo />
				)}
			</div>
			<div className="right-side">
				<Stack maw="400px" miw="358px" w="60%" align="center" m="auto" spacing="23px">
					<WhiteBox>
						<Stack spacing="7px" align="center" mb="24px">
							<Text variant="body">You&apos;ve been invited to collaborate on:</Text>
							<Text variant="h3">{name}</Text>
							{presenter.showInvitedBy && (
								<Group spacing="4px" align="center" noWrap>
									<Text>by</Text>
									<Text variant="h3">{presenter.invitedBy}</Text>
								</Group>
							)}
						</Stack>
						{!!presenter.formError && <ErrorCard>{presenter.formError}</ErrorCard>}
						<form onSubmit={presenter.onFormSubmitted}>
							<FormInput
								className={classNames( 'NameInput' )}
								{...presenter.nameInputProps}
							/>
							<Space h="24px" />
							<FormInput
								className={classNames( 'EmailInput' )}
								{...presenter.emailInputProps}
								informationTooltip={<GuestViewPreviewEmailTooltip />}
							/>
							<Space h="28px" />
							<Text variant="footnote" className="terms-and-conditions">
								By continuing as a guest, I agree to VeryBusy&apos;s
								<br />
								<a
									target="_blank"
									rel="noopener noreferrer"
									href={TERMS_AND_CONDITIONS_URL}
								>
									Terms & Conditions
								</a>
								&nbsp;and
								<a
									target="_blank"
									rel="noopener noreferrer"
									href={PRIVACY_POLICY_URL}
								>
									Privacy Policy
								</a>
							</Text>
							<Space h="16px" />
							<Button className="SubmitButton" disabled={false} testId="done-button">
								Continue as Guest
							</Button>
						</form>
						<p className="login">
							Already have an account?&nbsp;
							<button onClick={presenter.onRedirectToSignIn}>
								Login
							</button>
						</p>
					</WhiteBox>
					{!!presenter.showPowerdByVeryBusy && (
						<Group align="center" spacing="4px">
							<Text color="gray.6">Powered by</Text>
							<VeryBusyLogoGray />
						</Group>
					)}
				</Stack>
			</div>
		</div>
	);
};

export default observer( ProjectGuestViewPreview );
